import React, { useEffect, useState, useRef } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import styles from './MobileHomepage.module.css';
import { CCProvider, useCCContext } from '../CC/CCContext';
import * as ccUtils from '../CC/utils';
import MobileHomepageBlock from './MobileHomePageblock';
import AnnotatedImageViewer from '../AlbumEditor/AnnotatedImageViewer';
import GenericIcon from '../GenericComponents/GenericIcon';

type MobileHomepageProps = {
    mainY: number;
    setAndKeepMainY: (newMainY: number, prevMainY: number) => void;
};

const MobileHomepage: React.FC<MobileHomepageProps> = ({ mainY, setAndKeepMainY }) => {
    const { t, i18n } = useTranslation();
    const { cc, setCc, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData, isReady } = useCCContext();
    const ctx = useCCContext();
    const topRef = useRef<HTMLDivElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [introTextEnglish, setIntroTextEnglish] = useState<string>('');
    const [introTextFrench, setIntroTextFrench] = useState<string>('');
    const [imageUrl, setImageUrl] = useState<string | null>(null);

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
    };

    const showMenu = () => {
        setAndKeepMainY(-400, -100)
    }

    const showSearch = () => {
        setAndKeepMainY(-200, -100);
    }

    useEffect(() => {
        const getMonumentName = () => {
            const departmentFacet = cc.facets['department'];
            if (departmentFacet && departmentFacet.length === 1) {
                return departmentFacet[0];
            } else {
                const webdepCriteria = cc.customSearchCriteria.find(criteria => criteria.field === '/record/webdep');
                if (webdepCriteria && webdepCriteria.searchValues.length === 1) {
                    let value = webdepCriteria.searchValues[0];
                    if (value.startsWith('[') && value.endsWith(']')) {
                        value = value.slice(1, -1);  // Remove the first and last character
                    }
                    return value;
                }
            }
            return '';
        };

        const getMonumentTexts = async () => {
            try {
              const departmentFacet = cc.facets['department'];
              if (departmentFacet && departmentFacet.length === 1) {
                const monumentName = departmentFacet[0];
                const data = await ccUtils.fetchMonumentHtml(monumentName);
                if (data.records && data.records.record && data.records.record.data && data.records.record.data.record) {
                  const { introeng, introfra } = data.records.record.data.record;
                  setIntroTextEnglish(introeng || '');
                  setIntroTextFrench(introfra || '');
                } else {
                  console.error('Unexpected response structure:', data);
                }
              } else {
                const query = ccUtils.generateQuery(cc);
          
                if (query.includes('thème: Tapisseries') || query.includes('theme: Tapisseries')) {
                    setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>THE TAPESTRY COLLECTION</strong></p> <p align="LEFT" dir="LTR">Welcome to the database of the <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/tapisseries-la-collection" target="_blank">tapestries collection of the Centre des monuments nationaux</a>, one of the richest in France!<br />Discover the Apocalypse Tapestry presented at the Château d\'Angers or that of the History of Tancred and Clorinde preserved at the Château de Châteaudun and nearly a hundred other tapestries woven from the 14th to the 19th century during your navigation.<br />For each one, you will find a documentary notice and a few words about its history.</p></span>');
                    setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LA COLLECTION DES TAPISSERIES</strong></p> <p align="LEFT" dir="LTR">Bienvenue sur la base de données de <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/tapisseries-la-collection" target="_blank">la collection de tapisseries du Centre des monuments nationaux</a>, une des plus riches de France !<br />Découvrez la Tenture de l’Apocalypse présentée au château d’Angers ou encore celle de l\'Histoire de Tancrède et Clorinde conservée au château de Châteaudun et près d’une centaine autres tapisseries tissées du XIVe au XIXe siècle lors de votre navigation.<br />Pour chacune, vous trouverez une notice documentaire et quelques mots sur son histoire.<br /></p></span>');
                  } else if (query.includes('thème: Biens disparus') || query.includes('theme: Missing objects')) {
                    setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>LOST CULTURAL PROPERTY</strong></p> <p align="LEFT" dir="LTR">Welcome to the Centre des monuments nationaux database of lost or stolen cultural property.<br />This tool allows you to search <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/les-biens-disparus-de-nos-collections" target="_blank">the lost or stolen cultural property referenced in the CMN’s “Collectio” database</a>. <br />The items of property searched form part of the public domain of furniture (Article L 2112-1 of the French General Code of the Property of Public Persons – CG3P). These items of property are therefore inalienable, imprescriptible and exempt from seizure. <br />This search tool is intended for everyone, owners and custodians, assigning authorities, actors in the art trade including collectors, agencies combating the illicit trafficking in cultural property and customs controls. <br />It should be recalled that the information given is not exhaustive and may be enriched.</p></span>');
                    setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LES BIENS DISPARUS</strong></p> <p align="LEFT" dir="LTR">Bienvenue sur la base de données <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/les-biens-disparus-de-nos-collections" target="_blank">des biens culturels disparus ou volés du Centre des monuments nationaux</a>.<br />Cet outil permet la recherche des biens culturels volés ou disparus, référencés dans la base de données « Collectio » du Centre des monuments nationaux. <br />Les biens recherchés relèvent du domaine public mobilier (article L 2112-1 du code général de la propriété des personnes publiques – CG3P). Ces biens sont à ce titre inaliénables, imprescriptibles et insaisissables. <br />Cet outil de recherche est destiné à tous, propriétaires et dépositaires, affectataires, acteurs du marché de l&apos;art y compris collectionneurs, services de répression du trafic illicite des biens culturels ou de contrôle douanier. <br /><br />Il convient de rappeler que les informations présentées ne sont pas exhaustives et pourront être enrichies.</p></span>');
                  } else if (query.includes('thème: Photographies') || query.includes('theme: Photographs')) {
                    setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>PHOTOGRAPHY</strong></p><p align="LEFT" dir="LTR">Welcome to the database of photographs held by the Centre des monuments nationaux.<br />This tool allows you to search for photographs cataloged in the Centre des monuments nationaux’s &quot;Collectio&quot; inventory database.</p><p align="LEFT" dir="LTR">To mark the bicentenary of photography, over 1000 records covering black-and-white and color prints, photo albums, film negatives and glass plates, postcards, and photographic equipment are available for viewing. This little-known collection highlights various holdings from sites such as George Sand’s house in Nohant; the Château de Talcy, featuring daguerreotypes by Albert Stapfer (a pioneer of the medium in 1840); and Montmajour Abbey, with its collection of contemporary photographs.</p><p align="LEFT" dir="LTR">This section will be regularly updated with new records.</p></span>');
                    setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LA PHOTOGRAPHIE</strong></p><p align="LEFT" dir="LTR">Bienvenue sur la base de données des photographies conservées par le Centre des monuments nationaux.<br />Cet outil permet la recherche des photographies référencées dans la base d’inventaire « Collectio » du Centre des monuments nationaux.</p><p align="LEFT" dir="LTR">A l’occasion du bicentenaire de la Photographie, plus de 1000 notices de clichés noir et blanc ou couleur, d’albums photos, de négatifs souples ou plaques de verre, de cartes postales et de matériels photographiques sont consultables. Cette collection méconnue met en lumière plusieurs fonds provenant de monuments tels que la maison de George Sand à Nohant, le château de Talcy avec les daguerréotypes d&apos;Albert Stapfer, un des pionniers en 1840, ou encore l’abbaye de Montmajour avec une collection de photographies contemporaines.</p><p align="LEFT" dir="LTR">Ce thème sera régulièrement enrichi de nouvelles notices.</p></span>');
                  } else {
                    setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>THE COLLECTION OF THE CENTRE DES MONUMENTS NATIONAUX</strong></p> <p align="LEFT" dir="LTR">This database brings together the collections of a certain number of monuments from the Centre des monuments nationaux network. Others will gradually enrich this fund.</p></span>');
                    setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LES COLLECTIONS DU CENTRE DES MONUMENTS NATIONAUX</strong></p> <p align="LEFT" dir="LTR">Cette base rassemble les collections d’un certain nombre de monuments du réseau du Centre des monuments nationaux. D’autres seront amenées progressivement à enrichir ce fonds.</p></span>');
                  }
              }
            } catch (error) {
              console.error('Error fetching monument data:', error);
            }
          };    
          
          const getMonumentImage = async () => {
            try {
              const departmentFacet = cc.facets['department'];
              console.log("cc", JSON.stringify(cc));
              console.log("departmentFacet", departmentFacet);
          
              if (departmentFacet && departmentFacet.length === 1) {
                const monumentName = departmentFacet[0];
                const imageUrl = await ccUtils.fetchMonumentImage(monumentName);
                setImageUrl(imageUrl);
                console.log("setImageUrl", imageUrl);
              } else {
                const query = ccUtils.generateQuery(cc);
          
                if (query.includes('thème: Tapisseries') || query.includes('theme: Tapisseries')) {
                  setImageUrl('CMN_dbw16_0863.jpg');
                  console.log("setImageUrl", "tapis");
                } else if (query.includes('thème: Biens disparus') || query.includes('theme: Biens disparus')) {
                  setImageUrl('https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/034/images%20origine/Campagne%201983/ALC83-1420.jpg&width=600&_height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes'); 
                  console.log("setImageUrl", "biens disparus");
                } else if (query.includes('thème: Photographies') || query.includes('theme: Photographs')) {
                  setImageUrl('https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/040/Images%20pleine%20page/bgw16_0331.jpg&width=600&_height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes');
                  console.log("setImageUrl", "photographies");
                } else {
                  setImageUrl('121755--DBW23-0113.jpg');
                  console.log("setImageUrl", "homepage");
                }
              }
            } catch (error) {
              console.error('Error fetching monument data:', error);
            }
          }; 

        getMonumentTexts();
        getMonumentImage();
    }, [cc]);

    useEffect(() => {
        const handleResize = () => {
            const contentsElement = document.querySelector(`.${styles.contents}`) as HTMLElement;
            if (contentsElement) {
                contentsElement.style.height = `${window.innerHeight - 60}px`;
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial call to set the height

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div className={styles.MobileHomepage} >
            <div ref={topRef} className={styles.header}>
                <div className={styles.header_logo}
                    onClick={(e: any) => {
                        e.preventDefault();
                        setViewMode('homepage');
                    }}>
                    <div>
                        <div className={styles.header_logo_text}>Collections</div>
                        <div className={styles.header_logo_svg}>
                            <img src="header_logo.svg" />
                        </div>
                    </div>
                </div>
                <div className={styles.languageSelectorContainer}>
                    <select
                        className={styles.languageSelector}
                        value={i18n.language}
                        onChange={(event) => changeLanguage(event.target.value)}
                    >
                        <option value="en">English</option>
                        <option value="fr">Français</option>
                    </select>
                </div>
            </div>
            <div className={styles.contents}>
                <div className={styles.intro}>
                    <div className={styles.introText}>
                        {/* imageUrl && <img src={imageUrl} alt={"monument"} className={styles.monumentImage} /> */}
                        {imageUrl && <AnnotatedImageViewer imageName={imageUrl} />}
                        {i18n.language === 'en' ? <div className={styles.introTextText} dangerouslySetInnerHTML={{ __html: introTextEnglish }} /> : i18n.language === 'fr' ? <div className={styles.introTextText} dangerouslySetInnerHTML={{ __html: introTextFrench }} /> : ''}
                    </div>
                    <button onClick={(e) => { showSearch() }}>{t("homepage_click_to_view")}</button>
                </div>
                <div className={styles.homepageBlocks}>
                    <MobileHomepageBlock
                        onClick={(e) => {
                            e.preventDefault();
                            ccUtils.clearFacet(ctx, 'department');
                            if (i18n.language === 'fr') {
                                ccUtils.addCustomSearch(ctx, '/record/theme', 'Thème', ['[thème: Tapisseries]'], '');
                            } else {
                                ccUtils.addCustomSearch(ctx, '/record/themeeng', 'Theme', ['[theme: Tapisseries]'], '');
                            }

                            showSearch();
                        }}
                        imageUrl={"https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/033/images%20pleine%20page/CMN_dbw16_0865.jpg&width=350&_height=220&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                        text={t("homepageblock_tapisseries")}
                    />
                    <MobileHomepageBlock
                        onClick={(e) => {
                            e.preventDefault();
                            ccUtils.clearFacet(ctx, 'department');
                            if (i18n.language === 'fr') {
                                ccUtils.addCustomSearch(ctx, '/record/theme', 'Thème', ['[thème: Biens disparus]'], '');
                            } else {
                                ccUtils.addCustomSearch(ctx, '/record/themeeng', 'Theme', ['[theme: Missing objects]'], '');
                            }
                            showSearch();
                        }}
                        imageUrl={"https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/034/images%20origine/Campagne%201983/ALC83-1420_small.jpg&width=600&_height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                        text={t("homepageblock_biensdisparus")}
                    />
                    <MobileHomepageBlock
                        onClick={(e) => {
                            e.preventDefault();
                            ccUtils.clearFacet(ctx, 'department');
                            if (i18n.language === 'fr') {
                                ccUtils.addCustomSearch(ctx, '/record/theme', 'Thème', ['[thème: Photographies]'], '');
                            } else {
                                ccUtils.addCustomSearch(ctx, '/record/themeeng', 'Theme', ['[theme: Photographs]'], '');
                            }
                            showSearch();
                        }}
                        imageUrl={"https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/040/Images%20pleine%20page/bgw16_0331.jpg&width=350&_height=220&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                        text={t("homepageblock_photographies")}
                    />
                </div>

            </div>

            <button
                className={styles.infoButton}
                onClick={showMenu}>
                <GenericIcon icon='solid/info.svg' />
            </button>

        </div>
    )
};

export default MobileHomepage;
